// src/lib/filemaker-sync/types.ts
//
// Per-layout status of the FileMaker 19.2.2 <-> BENCHMARK
// bidirectional sync. Drives the admin /admin/filemaker-sync
// page and the customer portal "My Portal" fileMaker card.
//
// Mirrors the eleven layouts in Bench.jpg plus a small
// meta section (sync-agent heartbeat + queue depth).

import type { AuditEntry } from "@/lib/demoData";

export type FilemakerLayoutKey =
  | "customers"
  | "products"
  | "sales-orders"
  | "quotations"
  | "invoices"
  | "credit-notes"
  | "producers"
  | "banksters"
  | "lendings"
  | "repairs"
  | "reports";

export type SyncDirection = "fm_to_aws" | "aws_to_fm" | "bidirectional" | "fm_local_readonly";

export type SyncHealth = "ok" | "warning" | "error";

export interface FilemakerLayoutConfig {
  key: FilemakerLayoutKey;
  label: string;
  fmLayoutName: string;
  group: "Master data" | "Selling" | "Suppliers + banking" | "Operations" | "Reporting";
  direction: SyncDirection;
  description: string;
}

export const FILEMAKER_LAYOUTS: FilemakerLayoutConfig[] = [
  {
    key: "customers",
    label: "Customers",
    fmLayoutName: "Customer",
    group: "Master data",
    direction: "fm_to_aws",
    description:
      "B2B partner companies. Always created in FileMaker by the accountant or sales desk; mirrored to BENCHMARK within 60s.",
  },
  {
    key: "products",
    label: "Products",
    fmLayoutName: "Product",
    group: "Master data",
    direction: "fm_to_aws",
    description:
      "The 28-brand catalog. Soft-archive in FileMaker hides a product from new orders; existing order lines stay visible.",
  },
  {
    key: "sales-orders",
    label: "Sales Orders",
    fmLayoutName: "SalesOrder",
    group: "Selling",
    direction: "aws_to_fm",
    description:
      "Created in the BENCHMARK portal by the customer. Writeback to FileMaker within 60s. Status changes from FileMaker flow back status-only.",
  },
  {
    key: "quotations",
    label: "Quotations",
    fmLayoutName: "Quotation",
    group: "Selling",
    direction: "aws_to_fm",
    description:
      "Drafts in the portal; writeback fires when the customer accepts (status flips draft -> accepted).",
  },
  {
    key: "invoices",
    label: "Invoices",
    fmLayoutName: "Invoice",
    group: "Selling",
    direction: "fm_to_aws",
    description:
      "Issued by accountants in FileMaker. Read-only in the BENCHMARK portal; never created in the BENCHMARK app.",
  },
  {
    key: "credit-notes",
    label: "Credit Notes",
    fmLayoutName: "CreditNote",
    group: "Selling",
    direction: "fm_to_aws",
    description:
      "Partial or full credit notes against an invoice. Mirrored + surfaced in the portal under the related invoice.",
  },
  {
    key: "producers",
    label: "Producers (vendors)",
    fmLayoutName: "Producer",
    group: "Suppliers + banking",
    direction: "fm_to_aws",
    description:
      "Brand vendors. Producer.channel drives the WhatsApp/email choice for the vendor-ordering module; Producer.terms updates vendor_account.terms_snapshot.",
  },
  {
    key: "banksters",
    label: "Banksters (banks)",
    fmLayoutName: "Bankster",
    group: "Suppliers + banking",
    direction: "fm_to_aws",
    description:
      "Banking partners (CBC, ING visible in the home screen). Read-only mirror; needed for reconciliation.",
  },
  {
    key: "lendings",
    label: "Lendings",
    fmLayoutName: "Lending",
    group: "Suppliers + banking",
    direction: "fm_to_aws",
    description:
      "Loan accounts the business holds. Read-only mirror.",
  },
  {
    key: "repairs",
    label: "Repairs",
    fmLayoutName: "Repairs",
    group: "Operations",
    direction: "bidirectional",
    description:
      "BENCHMARK service-desk and customer-portal tickets write back to FileMaker. FileMaker-only repairs (in-house damaged shipments) mirror as customer-invisible unless explicitly marked.",
  },
  {
    key: "reports",
    label: "Reports",
    fmLayoutName: "Reports",
    group: "Reporting",
    direction: "fm_local_readonly",
    description:
      "Anonymised management accounts. Read-only mirror; OpenSearch indexes them downstream for Agent I (free-text search).",
  },
];

export const FILEMAKER_LAYOUT_BY_KEY: Record<FilemakerLayoutKey, FilemakerLayoutConfig> =
  Object.fromEntries(FILEMAKER_LAYOUTS.map((l) => [l.key, l])) as Record<
    FilemakerLayoutKey,
    FilemakerLayoutConfig
  >;

export type AnomalyKind =
  | "schema-drift"
  | "row-skipped"
  | "orphan-fk"
  | "currency-fx-stale"
  | "tier-mismatch"
  | "duplicate-key"
  | "missing-required-field"
  | "out-of-range";

export type AnomalySeverity = "low" | "medium" | "high" | "critical";

export interface SyncAnomaly {
  id: string;
  layoutKey: FilemakerLayoutKey;
  /** Source record identifier (FileMaker RecordID, when known). */
  sourcePk: string;
  kind: AnomalyKind;
  severity: AnomalySeverity;
  detectedAt: string;
  /** Operator-facing one-line description. */
  summary: string;
  /** Longer detail (the offending value, the expected range, etc.). */
  detail?: string;
  /** Suggested FileMaker field/area the operator should review. */
  suggestedFix?: string;
  resolvedAt?: string;
  resolvedBy?: string;
}

export interface LayoutSyncStatus {
  layoutKey: FilemakerLayoutKey;
  /** ISO datetime of the most recent successful mirror run. */
  lastSyncedAt: string;
  /** Rows updated/inserted on the last successful run. */
  lastRunDelta: number;
  /** Total rows mirrored to date for this layout. */
  totalRows: number;
  health: SyncHealth;
  /** Health detail message — shown under the layout name. */
  healthDetail?: string;
  /** Anomalies still open for this layout (filtered, not resolved). */
  openAnomalies: SyncAnomaly[];
}

export interface FilemakerSyncState {
  serverVersion: string;
  agentHeartbeatAt: string;
  /** Redpanda event queue depth (mirror + writeback combined). */
  queueDepth: number;
  /** Last 8 audit entries for the sync agent. */
  audit: AuditEntry[];
  layouts: Record<FilemakerLayoutKey, LayoutSyncStatus>;
}

export const ANOMALY_KIND_LABEL: Record<AnomalyKind, string> = {
  "schema-drift": "Schema drift",
  "row-skipped": "Row skipped",
  "orphan-fk": "Orphan FK",
  "currency-fx-stale": "FX rate stale",
  "tier-mismatch": "Tier mismatch",
  "duplicate-key": "Duplicate key",
  "missing-required-field": "Missing required field",
  "out-of-range": "Value out of range",
};

export const ANOMALY_SEVERITY_TONE: Record<AnomalySeverity, string> = {
  low: "bg-fg-quaternary/15 text-fg-tertiary",
  medium: "bg-accent-amber/15 text-accent-amber",
  high: "bg-accent-rose/15 text-accent-rose",
  critical: "bg-accent-rose/30 text-accent-rose",
};
